import { DropdownButtonType, List, ListItem, ListProps, SingleSelectDropdown, SingleSelectDropdownProps } from '@tableau/widgets';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IViewInfo } from '~src/views/models/IViewInfo';

export interface DevicePreviewNavigationToolsProps {
  viewList: IViewInfo[];
  startingViewIndex: number;
  onNewViewSelected: (newView: IViewInfo) => void;
}

export const DevicePreviewNavigationTools: React.FC<DevicePreviewNavigationToolsProps> = (props) => {
  const [currentViewIndex, setCurrentViewIndex] = useState(props.startingViewIndex);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sizeStyling: React.CSSProperties = {
    width: dropdownWidth,
    fontSize: 12,
    zIndex: 1,
  };

  const textStyling: React.CSSProperties = {
    fontWeight: 'normal',
    color: CommonStyles.Colors.Grey230,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...sizeStyling
  };

  const calculateDropdownWidth = useCallback(() => {
    if (!props.viewList || props.viewList.length === 0) {
      setDropdownWidth(0);
      return;
    }

    const span = document.createElement('span');
    document.body.appendChild(span);
    span.style.visibility = 'hidden';

    let maxWidth = 0;
    props.viewList.forEach(item => {
      span.textContent = item.name;
      const width = span.offsetWidth;
      maxWidth = Math.max(maxWidth, width);
    });

    document.body.removeChild(span);

    setDropdownWidth(maxWidth + 43);
  }, [props.viewList]);

  useEffect(() => {
    calculateDropdownWidth();
  }, [calculateDropdownWidth]);

  const getListItemsFromViews = (): ListItem[] => {
    return props.viewList.map((item) => ({
      value: item.name,
      testId: `device-preview-view-list-item-${item.name}`
    }));
  };

  const onDropdownValueChanged = (newValue: string) => {
    const newIndex = props.viewList.findIndex(view => view.name === newValue);
    setCurrentViewIndex(newIndex);
    props.onNewViewSelected(props.viewList[newIndex]);
  };

  if (props.viewList === undefined || props.viewList.length === 0) {
    return null;
  }

  const value = props.viewList[currentViewIndex].name;

  const listProps: ListProps = {
    items: getListItemsFromViews(),
    selectedValue: value
  };

  const dropdownProps: SingleSelectDropdownProps = {
    list: <List {...listProps} style={sizeStyling} />,
    onChange: onDropdownValueChanged,
    type: DropdownButtonType.Outline,
    useDarkStyling: true,
    value: value,
    testId: 'device-preview-dropdown-control',
    label: Messages.deviceLayoutViewDropdownLabel(),
  };

  return (
    <span className="tb-preview-navigation-tools-container">
      <div ref={dropdownRef}>
        <SingleSelectDropdown style={{ ...sizeStyling, width: dropdownWidth + 2 }} {...dropdownProps}>
          <span style={textStyling}>{value}</span>
        </SingleSelectDropdown>
      </div>
    </span>
  );
};